'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, DollarSign, Calculator, MapPin, Plane } from 'lucide-react';
import { useTranslation } from "@/hooks/useTranslation";
import { convertToLocaleNumber, formatCurrency, formatPercentage } from "@/lib/i18n/numbers";
import { highlightKeywords } from "@/lib/utils";

const getCostFactors = (t: any) => [
    {
        icon: DollarSign,
        title: t('cost.factors.numberOfImplants.title'),
        description: t('cost.factors.numberOfImplants.description')
    },
    {
        icon: Calculator,
        title: t('cost.factors.typeOfProsthetic.title'),
        description: t('cost.factors.typeOfProsthetic.description')
    },
    {
        icon: MapPin,
        title: t('cost.factors.additionalProcedures.title'),
        description: t('cost.factors.additionalProcedures.description')
    },
    {
        icon: Plane,
        title: t('cost.factors.travelPackage.title'),
        description: t('cost.factors.travelPackage.description')
    }
];


function useAnimatedCounter(targetValue: number, duration: number = 2000) {
    const [displayValue, setDisplayValue] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);

    const animate = () => {
        if (isAnimating) return;

        setIsAnimating(true);
        setDisplayValue(0);

        const startTime = Date.now();
        const startValue = 0;

        const updateCounter = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);

            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            const currentValue = Math.floor(startValue + (targetValue - startValue) * easeOutQuart);

            setDisplayValue(currentValue);

            if (progress < 1) {
                requestAnimationFrame(updateCounter);
            } else {
                setIsAnimating(false);
            }
        };

        requestAnimationFrame(updateCounter);
    };

    return { displayValue, animate };
}

export default function CostSection() {
    const { t, isRTL, locale } = useTranslation("all-on-4");
    const costFactors = getCostFactors(t);
    
    const highlightBySection = (text: string, section: string = 'included') => {
        const keyword = t(`cost.keywords.${section}`);
        if (!keyword) {
            if (process.env.NODE_ENV !== 'production') {
                console.warn(`[highlightBySection] Missing translation key: cost.keywords.${section}`);
            }
            return [text];
        }
        return highlightKeywords(text, [keyword]);
    };
    const AnimatedPriceRange = ({
        min,
        max,
        className = "",
        prefix = "€"
    }: {
        min: number;
        max: number;
        className?: string;
        prefix?: string;
    }) => {
        const minCounter = useAnimatedCounter(min, 1500);
        const maxCounter = useAnimatedCounter(max, 1500);

        return (
            <motion.div
                className={className}
                onViewportEnter={() => {
                    minCounter.animate();
                    maxCounter.animate();
                }}
                viewport={{ once: false, margin: "-100px" }}
            >
                {formatCurrency(minCounter.displayValue, locale)} - {formatCurrency(maxCounter.displayValue, locale)}
            </motion.div>
        );
    };

    const AnimatedSavings = ({ percentage }: { percentage: number }) => {
        const counter = useAnimatedCounter(percentage, 1500);

        return (
            <motion.div
                onViewportEnter={() => {
                    counter.animate();
                }}
                viewport={{ once: false, margin: "-100px" }}
                className="text-4xl lg:text-5xl font-bold text-green-600 dark:text-green-400"
            >
                {formatPercentage(counter.displayValue, locale)}
            </motion.div>
        );
    };

    const handleConsultationClick = () => {
        const candidatesSection = document.getElementById('candidates');
        candidatesSection?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <section
            id="cost"
            className="py-16 lg:py-24 bg-white dark:bg-slate-800"
        >
            <div className="container">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif leading-tight mb-6">
                        {highlightBySection(t("cost.title"), 'investment')}
                    </h2>
                    <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
                        {t("cost.subtitle")}
                    </p>
                </div>

                <div className="grid lg:grid-cols-3 gap-8 mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                    >
                        <Card className="h-full border-slate-200 dark:border-slate-700 hover:shadow-xl transition-shadow duration-300 min-h-[400px]">
                            <CardContent className="p-8 text-center">
                                <h3 className="text-2xl font-bold text-navy dark:text-white mb-4 text-center">
                                    {t("cost.packages.allOn4.title")}
                                </h3>
                                <AnimatedPriceRange
                                    min={4500}
                                    max={6500}
                                    className="text-3xl lg:text-4xl font-bold text-gold mb-4 text-center"
                                />
                                <p className={`text-slate-600 dark:text-slate-300 mb-6 ${isRTL ? 'text-right' : 'text-center'}`}>
                                    {t("cost.priceRange.completePackage")}
                                </p>
                                <ul className={`space-y-2 text-sm ${isRTL ? 'text-right' : 'text-left'}`}>
                                    {(Array.isArray(t("cost.packages.allOn4.features", { returnObjects: true })) ? t("cost.packages.allOn4.features", { returnObjects: true }) : []).map((feature: string, index: number) => (
                                        <li key={index} className={`flex items-center ${isRTL ? 'flex-row-reverse justify-end' : 'justify-start'}`}>
                                            <span>{feature}</span>
                                            <CheckCircle className={`w-4 h-4 text-gold ${isRTL ? 'mr-2' : 'ml-2'}`} />
                                        </li>
                                    ))}
                                </ul>
                            </CardContent>
                        </Card>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        viewport={{ once: true }}
                    >
                        <Card className="h-full border-gold border-2 hover:shadow-xl transition-shadow duration-300 relative min-h-[400px]">
                            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                <span className="bg-gold text-navy px-4 py-1 rounded-full text-sm font-medium">
                                    {t("cost.packages.allOn6.mostPopular")}
                                </span>
                            </div>
                            <CardContent className="p-8 text-center">
                                <h3 className="text-2xl font-bold text-navy dark:text-white mb-4 text-center">
                                    {t("cost.packages.allOn6.title")}
                                </h3>
                                <AnimatedPriceRange
                                    min={6500}
                                    max={8500}
                                    className="text-3xl lg:text-4xl font-bold text-gold mb-4 text-center"
                                />
                                <p className={`text-slate-600 dark:text-slate-300 mb-6 ${isRTL ? 'text-right' : 'text-center'}`}>
                                    {t("cost.priceRange.completePackage")}
                                </p>
                                <ul className={`space-y-2 text-sm ${isRTL ? 'text-right' : 'text-left'}`}>
                                    {(Array.isArray(t("cost.packages.allOn6.features", { returnObjects: true })) ? t("cost.packages.allOn6.features", { returnObjects: true }) : []).map((feature: string, index: number) => (
                                        <li key={index} className={`flex items-center ${isRTL ? 'flex-row-reverse justify-end' : 'justify-start'}`}>
                                            <span>{feature}</span>
                                            <CheckCircle className={`w-4 h-4 text-gold ${isRTL ? 'mr-2' : 'ml-2'}`} />
                                        </li>
                                    ))}
                                </ul>
                            </CardContent>
                        </Card>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        viewport={{ once: true }}
                    >
                        <Card className="h-full bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200 dark:border-green-700">
                            <CardContent className="p-8 text-center">
                                <h3 className="text-2xl font-bold text-navy dark:text-white mb-4 text-center">
                                    {t("cost.packages.comparison.savings")}
                                </h3>
                                <div className="text-center">
                                    <AnimatedSavings percentage={70} />
                                </div>
                                <p className={`text-green-700 dark:text-green-300 font-medium mb-6 ${isRTL ? 'text-right' : 'text-center'}`}>
                                    vs UK/US prices
                                </p>
                                <div className={`text-sm space-y-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                                    <div className="flex justify-between">
                                        <span>{t("cost.packages.comparison.ukPrice")}:</span>
                                        <span className="line-through">{formatCurrency(20000, locale)}+</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>US Price:</span>
                                        <span className="line-through">{formatCurrency(25000, locale)}+</span>
                                    </div>
                                    <div className="flex justify-between font-bold text-green-600">
                                        <span>{t("cost.packages.comparison.istanbulPrice")}:</span>
                                        <span>{formatCurrency(6500, locale)}</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                </div>

                <div className="mb-16">
                    <h3 className="text-2xl lg:text-3xl font-bold text-navy dark:text-white text-center mb-12">
                        {highlightBySection(t("cost.factors.title"), 'cost')}
                    </h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {costFactors.map((factor, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1, duration: 0.5 }}
                                viewport={{ once: true }}
                            >
                                <Card className="h-full border-slate-200 dark:border-slate-700 hover:shadow-lg transition-shadow duration-300">
                                    <CardContent className="p-6 text-center">
                                        <div className="w-12 h-12 bg-gold/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                                            <factor.icon className="w-6 h-6 text-gold" />
                                        </div>
                                        <h4 className="text-lg font-semibold text-navy dark:text-white mb-3">
                                            {factor.title}
                                        </h4>
                                        <p className="text-slate-600 dark:text-slate-300 text-sm">
                                            {factor.description}
                                        </p>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                </div>

                <Card className="bg-slate-50 dark:bg-navy-950 border-slate-200 dark:border-slate-700 mb-12">
                    <CardContent className="p-5 sm:p-8 lg:p-12">
                        <h3 className="text-2xl lg:text-3xl font-bold text-navy dark:text-white text-center mb-8">
                            {highlightBySection(t("cost.included.title"))}
                        </h3>
                        <div className="grid md:grid-cols-2 gap-6">
                            {(Array.isArray(t("cost.included.services", { returnObjects: true })) ? t("cost.included.services", { returnObjects: true }) : []).map((service: string, index: number) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.1, duration: 0.5 }}
                                    viewport={{ once: true }}
                                    className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}
                                >
                                    <CheckCircle className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                                    <span className="text-slate-600 dark:text-slate-300 flex-1">{service}</span>
                                </motion.div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                <div className="text-center">
                    <Button
                        onClick={handleConsultationClick}
                        className="bg-gold hover:bg-gold-600 text-navy font-semibold px-8 py-3 rounded-lg text-lg"
                    >
                        {t("cost.cta.button")}
                    </Button>
                    <p className="text-slate-600 dark:text-slate-300 text-sm mt-4">
                        {t("cost.cta.disclaimer")}
                    </p>
                </div>
            </div>
        </section>
    );
} 